import { TABLE_CELL_PADDING_CLASS, TABLE_HEADER_CELL_CLASS } from '@/components/ui/table-tokens'
import { cn } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Header } from '@tanstack/react-table'
import { ArrowDown, ArrowDownUp, ArrowUp, GripVertical } from 'lucide-react'
import type React from 'react'
import { ColumnHeaderLabel } from './ColumnHeaderLabel'
import { getAlignmentClasses, getColumnSortLabel } from './TableView'
interface DraggableColumnHeaderProps<TData> {
	header: Header<TData, unknown>
	columnWidth: number // width of the column header , calculated by tableview
	isFrozen: boolean
	isFrozenRight?: boolean // whether the column is frozen on the right side
	leftOffset: number // left offset for frozen columns, calculated by tableview
	rightOffset?: number // right offset for right-frozen columns, calculated by tableview
	isDraggable: boolean
	isTouchDevice?: boolean
	alignment?: string
	fixedWidth?: boolean
	isFirstRightFrozen?: boolean // whether this is the first right-frozen column (for margin-left: auto)
	isLastColumn?: boolean // whether this is the last column (for flex-1)
	dataTestId?: string
}

export function DraggableColumnHeader<TData>({
	header,
	columnWidth,
	isFrozen,
	isFrozenRight = false,
	leftOffset,
	rightOffset = 0,
	isDraggable,
	alignment,
	isFirstRightFrozen = false,
	isLastColumn = false,
	dataTestId,
}: DraggableColumnHeaderProps<TData>) {
	const {
		attributes, // ARIA attributes for accessibility
		listeners, // Event handlers for drag interactions
		setNodeRef, // Ref to attach to the draggable element
		transform, // Current drag transform (x, y translation)
		transition, // CSS transition for smooth animations
		isDragging, // Boolean indicating if this element is currently being dragged
	} = useSortable({
		id: header.column.id, // Unique ID for the sortable item
		disabled: !isDraggable, // Disable drag for frozen columns
	})

	// Sorting state
	const canSort = header.column.getCanSort()
	const sortDirection = header.column.getIsSorted()
	const isSelectColumn = header.column.id === 'select'

	const handleSortClick = (event: React.MouseEvent) => {
		event.preventDefault()
		event.stopPropagation()

		if (canSort && !isSelectColumn) {
			header.column.toggleSorting()
		}
	}

	const handleSortKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			event.stopPropagation()

			if (canSort && !isSelectColumn) {
				header.column.toggleSorting()
			}
		}
	}

	// Converting dnd-kit transform to CSS transform string
	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		width: `${columnWidth}px`,
		minHeight: '36px',
		touchAction: isDraggable ? 'none' : 'auto', // prevents touch scrolling when dragging
		...(isFirstRightFrozen && {
			marginLeft: 'auto',
		}),
		...(isFrozen &&
			!isFrozenRight && {
				position: 'sticky' as const,
				left: `${leftOffset}px`,
				zIndex: isDragging ? 30 : 20, // above header when dragging, below when not
			}),
		...(isFrozenRight && {
			position: 'sticky' as const,
			right: `${rightOffset}px`,
			zIndex: isDragging ? 30 : 20,
		}),
	}

	// Sort icon to show
	const showActiveSort = canSort && !isSelectColumn && sortDirection
	const showHoverSort = canSort && !isSelectColumn && !sortDirection

	const ActiveSortIcon = sortDirection === 'asc' ? ArrowUp : ArrowDown
	// Get alignment classes - fallback to 'left' if alignment is not provided
	const finalAlignment = alignment || 'left'
	const alignmentClasses = getAlignmentClasses(finalAlignment)

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn(
				cn('group relative flex items-center py-2 shrink-0', TABLE_HEADER_CELL_CLASS),
				isFrozen ? 'frozen-header-solid' : 'bg-table-header-bg',
				alignmentClasses,
				isDragging ? 'opacity-50 shadow-lg z-30' : '',
				isLastColumn ? 'flex-1' : '',
			)}
			data-column-id={header.column.id}
			{...attributes}
			{...(isDraggable ? listeners : {})}>
			{/* Drag grip only on draggable columns, show on hover */}
			{isDraggable && (
				<div
					{...listeners}
					style={{ touchAction: 'none' }}
					className='absolute left-1 top-1/2 transform -translate-y-1/2 w-4 flex justify-center cursor-grab active:cursor-grabbing'>
					<GripVertical
						className={
							'text-muted-foreground hover:text-foreground transition-all opacity-0 group-hover:opacity-100 size-(--icon-standalone)'
						}
					/>
				</div>
			)}

			{/* Header clickable for sorting - Use the same alignment classes */}
			<div
				className={cn(
					'flex justify-center items-center flex-1 min-w-0',
					isSelectColumn ? 'px-2' : TABLE_CELL_PADDING_CLASS,
					canSort && !isSelectColumn ? 'cursor-pointer' : '',
					alignmentClasses,
				)}
				onClick={handleSortClick}
				onKeyDown={handleSortKeyDown}
				tabIndex={canSort && !isSelectColumn ? 0 : -1}
				role={canSort && !isSelectColumn ? 'button' : undefined}
				aria-label={canSort && !isSelectColumn ? `Sort by ${getColumnSortLabel(header)}` : undefined}
				data-testid={dataTestId ? `${dataTestId}-header-${header.column.id}` : undefined}>
				{header.isPlaceholder ? null : (
					<ColumnHeaderLabel
						header={header}
						align={finalAlignment as 'left' | 'center' | 'right'}
						dataTestId={dataTestId}
					/>
				)}
			</div>

			{/* Sort indicators */}
			<div
				className='absolute right-2 top-1/2 transform -translate-y-1/2 w-4 flex justify-center cursor-pointer'
				onClick={handleSortClick}
				onKeyDown={handleSortKeyDown}
				tabIndex={canSort && !isSelectColumn ? 0 : -1}
				role={canSort && !isSelectColumn ? 'button' : undefined}
				aria-label={canSort && !isSelectColumn ? `Sort by ${getColumnSortLabel(header)}` : undefined}>
				{/* Active sort arrow is displayed only when column is sorted */}
				{showActiveSort && <ActiveSortIcon className='size-(--icon-standalone) text-foreground' />}

				{/* sort indicator is displayed only on hover when not sorted */}
				{showHoverSort && (
					<ArrowDownUp className='size-(--icon-standalone) text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity' />
				)}
			</div>
		</div>
	)
}
